import { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import ProposalOrderHeader from "./ProposalOrderHeader";
import ProposalOrderClientCard from "./ProposalOrderClientCard";
import ProposalOrderWineList from "./ProposalOrderWineList";
import ProposalOrderForm from "./ProposalOrderForm";
import ProposalOrderPaymentMethod from "./ProposalOrderPaymentMethod";
import ProposalOrderConfirmModal from "./ProposalOrderConfirmModal";
import { RiShoppingBag3Line } from "react-icons/ri";
import { TiTick } from "react-icons/ti";

const OrdersProposal = ({ hasPermission, proposalId: propId }) => {
    const { slug2 } = useParams();
    const navigate = useNavigate();

    const activeProposalId = propId || slug2;
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");

    const canEdit = Boolean(hasPermission?.("Proposals", "edit")) || !hasPermission;

    // Loading states
    const [isLoading, setIsLoading] = useState(true);

    // Proposal Data
    const [proposal, setProposal] = useState(null);
    const [wines, setWines] = useState([]);

    // Check if proposal has already been confirmed/ordered
    const isAlreadyOrdered = useMemo(() => {
        return Boolean(
            proposal?.is_already_ordered ||
            String(proposal?.status || "").toUpperCase() === "ORDERED"
        );
    }, [proposal]);

    // Check if proposal is cancelled
    const isCancelled = useMemo(() => {
        return String(proposal?.status || "").toUpperCase() === "CANCELLED";
    }, [proposal]);

    // Provinces Data
    const [provincesList, setProvincesList] = useState([]);

    // Form Validation Errors
    const [formErrors, setFormErrors] = useState({});

    // Payment Condition Default
    const [defaultPaymentCondition, setDefaultPaymentCondition] = useState("");

    // Address Mode: 'existing' | 'new'
    const [addressMode, setAddressMode] = useState("existing");
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState("primary");
    const [savedClientAddress, setSavedClientAddress] = useState(null);

    // Order Form Data
    const [formData, setFormData] = useState({
        order_date: dayjs().format("YYYY-MM-DD"),
        delivery_date: "",
        reference: "",
        company_name_recipient: "",
        address_title: "",
        destination_address: "",
        destination_city: "",
        destination_postal_code: "",
        destination_province: "",
        destination_country: "Italia",
        destination_phone: "",
        destination_mobile_phone: "",
        destination_fax: "",
        destination_email: "",
        delivery_notes: "",
        notes: "",
        payment_conditions_code: "",
    });


    // 1. Fetch Proposal Details
    const fetchProposal = useCallback(async () => {
        if (!activeProposalId) return;
        setIsLoading(true);
        try {
            const response = await axios.get(
                `${apiUrl}/api/v1/proposals/${activeProposalId}/order-preview`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response?.data?.success) {
                const data = response.data?.data || {};

                // Map proposal & restaurant from order-preview structure
                const proposalInfo = data.proposal || {};
                const restaurantInfo = data.restaurant || {};
                const mergedProposal = {
                    ...data,
                    ...proposalInfo,
                    restaurant: restaurantInfo,
                };

                setProposal(mergedProposal);
                setWines(data?.wines || []);

                // Pre-populate form data matching order-preview
                const target = restaurantInfo;
                const resolvedDeliveryNotes = target.delivery_notes || "";
                const resolvedCompanyName = proposalInfo.display_client_name || target.company_name || "";
                const resolvedPaymentCondition = data?.default_payment_method?.code || "";

                setDefaultPaymentCondition(resolvedPaymentCondition);

                // Delivery addresses directly from API (no extra mapping)
                const deliveryAddresses = Array.isArray(data.delivery_addresses)
                    ? data.delivery_addresses
                    : [];

                const defaultAddress =
                    deliveryAddresses.find((a) => a?.is_legal_default) ||
                    deliveryAddresses[0] ||
                    null;

                setSavedAddresses(deliveryAddresses);
                setSelectedAddressId(defaultAddress?.id ?? 0);
                setSavedClientAddress(defaultAddress);
                setAddressMode("existing");

                setFormData((prev) => ({
                    ...prev,
                    reference: `PROP-${proposalInfo.id || activeProposalId}`,
                    delivery_notes: resolvedDeliveryNotes,
                    notes: `Ordine generato a partire dalla proposta #${proposalInfo.id || activeProposalId}`,
                    payment_conditions_code: resolvedPaymentCondition,
                    company_name_recipient: resolvedCompanyName,
                    address_title: defaultAddress?.address_title || "",
                    destination_address: defaultAddress?.address || "",
                    destination_city: defaultAddress?.city || "",
                    destination_postal_code: defaultAddress?.postal_code || "",
                    destination_province: defaultAddress?.province || "",
                    destination_phone: defaultAddress?.telephone || "",
                    destination_mobile_phone: defaultAddress?.mobile_no || "",
                    destination_fax: defaultAddress?.fax || "",
                    destination_email: defaultAddress?.email || "",
                }));
            } else {
                console.error(
                    "Error loading proposal preview:",
                    response?.data?.message || "Errore nel caricamento dei dati della proposta."
                );
            }
        } catch (error) {
            console.error("Error fetching proposal details:", error);
            toast.error("Errore nel caricamento dei dati della proposta.", { theme: "colored" });
        } finally {
            setIsLoading(false);
        }
    }, [activeProposalId, apiUrl, token]);

    // 2. Fetch Provinces List
    const fetchProvinces = useCallback(async () => {
        try {
            const response = await axios.get(`${apiUrl}/api/v1/provinces`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setProvincesList(response?.data?.data || response?.data || []);
        } catch (error) {
            console.error("Error fetching provinces:", error);
        }
    }, [apiUrl, token]);

    // 3. Fetch Payment Methods List (for human-readable name resolution)
    const [paymentMethodsList, setPaymentMethodsList] = useState([]);

    const fetchPaymentMethods = useCallback(async () => {
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(`${apiUrl}/api/v1/payment-methods`, { headers });
            if (response?.status === 200) {
                setPaymentMethodsList(response?.data?.data || []);
            }
        } catch (error) {
            console.error("Error fetching payment methods:", error);
        }
    }, [apiUrl, token]);

    useEffect(() => {
        fetchProposal();
        fetchProvinces();
        fetchPaymentMethods();
    }, [fetchProposal, fetchProvinces, fetchPaymentMethods]);

    // Handle address mode switch: 'existing' vs 'new'
    const handleAddressModeChange = (nextMode) => {
        if (nextMode === addressMode) return;

        // Reset errors when switching mode
        setFormErrors({});

        if (nextMode === "new") {
            // Switch to 'new' mode: Reset all form fields to completely empty
            setAddressMode("new");
            setSelectedAddressId("new");
            setFormData((prev) => ({
                ...prev,
                company_name_recipient: "",
                address_title: "",
                destination_address: "",
                destination_city: "",
                destination_postal_code: "",
                destination_province: "",
                destination_country: "Italia",
                destination_phone: "",
                destination_mobile_phone: "",
                destination_fax: "",
                destination_email: "",
            }));
        } else {
            // Switching back to 'existing'
            setAddressMode("existing");

            // Restore saved client address
            const targetAddr =
                savedClientAddress ||
                (Array.isArray(savedAddresses)
                    ? savedAddresses.find(
                          (a, idx) =>
                              String(a?.id ?? idx) === String(selectedAddressId ?? 0)
                      ) || savedAddresses[0]
                    : null);

            setSelectedAddressId(targetAddr?.id ?? 0);
            setSavedClientAddress(targetAddr || null);

            if (targetAddr) {
                setFormData((prev) => ({
                    ...prev,
                    address_title: targetAddr.address_title || "",
                    destination_address: targetAddr.address || "",
                    destination_city: targetAddr.city || "",
                    destination_postal_code: targetAddr.postal_code || "",
                    destination_province: targetAddr.province || "",
                    destination_phone: targetAddr.telephone || "",
                    destination_mobile_phone: targetAddr.mobile_no || "",
                    destination_fax: targetAddr.fax || "",
                    destination_email: targetAddr.email || "",
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    address_title: "",
                    destination_address: "",
                    destination_city: "",
                    destination_postal_code: "",
                    destination_province: "",
                    destination_phone: "",
                    destination_mobile_phone: "",
                    destination_fax: "",
                    destination_email: "",
                }));
            }
        }
    };

    // Handle form inputs change
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear error as user types into this field
        if (formErrors[name]) {
            setFormErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    // Handle selection of a specific saved address card
    const handleSelectSavedAddress = (addr, idx) => {
        setFormErrors({});
        setSelectedAddressId(addr?.id ?? idx ?? 0);
        setSavedClientAddress(addr);
        setFormData((prev) => ({
            ...prev,
            address_title: addr?.address_title || "",
            destination_address: addr?.address || "",
            destination_city: addr?.city || "",
            destination_postal_code: addr?.postal_code || "",
            destination_province: addr?.province || "",
            destination_phone: addr?.telephone || "",
            destination_mobile_phone: addr?.mobile_no || "",
            destination_fax: addr?.fax || "",
            destination_email: addr?.email || "",
        }));
    };

    // Handle Final Action: Confirm Order (Conferma Ordine) with Confirmation Modal
    const [isSaving, setIsSaving] = useState(false);
    const [validationTrigger, setValidationTrigger] = useState(0);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    // 1. Initial Validation: verify form and open Confirmation Modal
    const handleInitiateConfirmOrder = () => {
        if (!canEdit || isAlreadyOrdered || isCancelled) {
            if (isCancelled) {
                toast.error("Impossibile confermare: la proposta è stata annullata.", { theme: "colored" });
            } else if (isAlreadyOrdered) {
                toast.info("Questo ordine è già stato confermato.", { theme: "colored" });
            } else {
                toast.error("Non hai i permessi per confermare l'ordine.", { theme: "colored" });
            }
            return;
        }

        // Validate mandatory address fields ONLY when in 'new' address mode
        if (addressMode === "new") {
            const errors = {};
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            if (!formData.address_title || !String(formData.address_title).trim()) {
                errors.address_title = "Il titolo dell'indirizzo è obbligatorio";
            }
            if (!formData.destination_address || !String(formData.destination_address).trim()) {
                errors.destination_address = "L'indirizzo è obbligatorio";
            }
            if (!formData.destination_city || !String(formData.destination_city).trim()) {
                errors.destination_city = "La città è obbligatoria";
            }
            if (!formData.destination_postal_code || !String(formData.destination_postal_code).trim()) {
                errors.destination_postal_code = "Il CAP è obbligatorio";
            }
            if (!formData.destination_province || !String(formData.destination_province).trim()) {
                errors.destination_province = "La provincia è obbligatoria";
            }
            if (!formData.destination_phone || !String(formData.destination_phone).trim()) {
                errors.destination_phone = "Il numero di telefono è obbligatorio";
            }
            if (!formData.destination_mobile_phone || !String(formData.destination_mobile_phone).trim()) {
                errors.destination_mobile_phone = "Il numero di cellulare è obbligatorio";
            }
            const emailVal = String(formData.destination_email || "").trim();
            if (!emailVal) {
                errors.destination_email = "L'indirizzo email è obbligatorio";
            } else if (!emailRegex.test(emailVal)) {
                errors.destination_email = "Inserisci un indirizzo email valido";
            }
            if (!formData.destination_fax || !String(formData.destination_fax).trim()) {
                errors.destination_fax = "Il numero di fax è obbligatorio";
            }

            if (Object.keys(errors).length > 0) {
                setFormErrors(errors);
                setValidationTrigger((prev) => prev + 1);
                return;
            }
        }

        // Validate payment conditions
        const resolvedPaymentCondition = formData.payment_conditions_code || defaultPaymentCondition;
        if (!resolvedPaymentCondition) {
            setFormErrors((prev) => ({
                ...prev,
                payment_conditions_code: "Seleziona una condizione di pagamento",
            }));
            setValidationTrigger((prev) => prev + 1);
            return;
        }

        // Validation passed: open confirmation modal
        setFormErrors({});
        setIsConfirmModalOpen(true);
    };

    // 2. Final Execution: user confirms via Confirmation Modal
    const handleExecuteConfirmOrder = async () => {
        if (isSaving) return;
        setIsSaving(true);

        const resolvedPaymentCondition = String(
            formData.payment_conditions_code || defaultPaymentCondition || ""
        ).trim();

        let payload;

        if (addressMode === "new" || selectedAddressId === "new") {
            // Case 3: if user add new address then only
            payload = {
                delivery_address_id: "new",
                new_delivery_address: {
                    address_title: formData.address_title || "",
                    address: formData.destination_address || "",
                    city: formData.destination_city || "",
                    postal_code: formData.destination_postal_code || "",
                    province: formData.destination_province || "",
                    telephone: formData.destination_phone || "",
                    mobile_no: formData.destination_mobile_phone || "",
                    email: formData.destination_email || "",
                    fax: formData.destination_fax || "",
                },
                payment_condition_code: resolvedPaymentCondition,
            };
        } else if (
            savedClientAddress?.id != null &&
            Number(savedClientAddress.id) > 0
        ) {
            // Case 1: if user select any exiting address then pass
            const parsedId = Number(savedClientAddress.id);
            payload = {
                delivery_address_id: parsedId,
                payment_condition_code: resolvedPaymentCondition,
            };
        } else {
            // Case 2: if address id is null (default legal address) or not any address select
            payload = {
                payment_condition_code: resolvedPaymentCondition,
            };
        }

        try {
            const response = await axios.post(
                `${apiUrl}/api/v1/proposals/${activeProposalId}/convert-to-order`,
                payload,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response?.data?.success) {
                const dynamicMessage =
                    response.data.message || "Proposal converted to order successfully.";
                toast.success(dynamicMessage, { theme: "colored" });
                setIsConfirmModalOpen(false);
                navigate("/admin/proposals");
            } else {
                const dynamicMessage =
                    response?.data?.message || "Proposal converted to order successfully.";
                toast.success(dynamicMessage, { theme: "colored" });
                setIsConfirmModalOpen(false);
                navigate("/admin/proposals");
            }
        } catch (error) {
            console.error("Error converting proposal to order:", error);
            const errMsg =
                error?.response?.data?.message ||
                error?.message ||
                "Errore durante la conversione in ordine.";
            toast.error(errMsg, { theme: "colored" });
        } finally {
            setIsSaving(false);
        }
    };

    // Modal Summary 1: Company Name
    const modalCompanyName = useMemo(() => {
        return (
            proposal?.restaurant?.company_name ||
            proposal?.display_client_name ||
            proposal?.restaurant?.name ||
            formData.company_name_recipient ||
            "Cliente Metèri"
        );
    }, [proposal, formData.company_name_recipient]);

    // Modal Summary 2: Selected Full Address
    const modalAddress = useMemo(() => {
        const currentSaved =
            savedClientAddress ||
            (Array.isArray(savedAddresses)
                ? savedAddresses.find(
                      (a, idx) => String(a?.id ?? idx) === String(selectedAddressId ?? 0)
                  ) || savedAddresses[0]
                : null);

        const title = formData.address_title || currentSaved?.address_title || "";
        const street = formData.destination_address || currentSaved?.address || "";
        const city = formData.destination_city || currentSaved?.city || "";
        const cap = formData.destination_postal_code || currentSaved?.postal_code || "";
        const prov = formData.destination_province || currentSaved?.province || "";
        const country = formData.destination_country || currentSaved?.country || "Italia";

        let formatted = "";
        if (addressMode === "existing" && currentSaved?.formatted_full_address) {
            formatted = currentSaved.formatted_full_address;
        } else {
            const locParts = [cap, city, prov ? `(${prov})` : ""].filter(Boolean).join(" ");
            formatted = [street, locParts, country].filter(Boolean).join(", ");
        }

        const email = formData.destination_email || currentSaved?.email || "";
        const phone = formData.destination_phone || currentSaved?.telephone || "";
        const mobile = formData.destination_mobile_phone || currentSaved?.mobile_no || "";
        const fax = formData.destination_fax || currentSaved?.fax || "";

        return {
            title: title || (addressMode === "existing" ? "Indirizzo Esistente" : "Nuovo Indirizzo"),
            full: formatted || street || "Indirizzo non specificato",
            email,
            phone,
            mobile,
            fax,
        };
    }, [formData, savedClientAddress, savedAddresses, selectedAddressId, addressMode]);

    // Modal Summary 3: Payment Method Selected Name
    const modalPaymentMethodName = useMemo(() => {
        const activeCode = String(formData.payment_conditions_code || defaultPaymentCondition || "").trim();
        if (!activeCode) return "Condizione non specificata";
        const found = paymentMethodsList.find(
            (m) => m?.code != null && String(m.code).trim() === activeCode
        );
        return found?.name || found?.description || `Condizione #${activeCode}`;
    }, [formData.payment_conditions_code, defaultPaymentCondition, paymentMethodsList]);

    // Modal Summary 4: Total Wine Bottles
    const modalTotalBottles = useMemo(() => {
        if (proposal?.financial_summary?.total_bottles != null) {
            return proposal.financial_summary.total_bottles;
        }
        return wines.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0);
    }, [proposal, wines]);

    // Modal Summary 5: Total Order Price
    const modalTotalPrice = useMemo(() => {
        return (
            proposal?.financial_summary?.subtotal_net_formatted ||
            proposal?.financial_summary?.total_gross_formatted ||
            "0,00 €"
        );
    }, [proposal]);

    return (
        <div className="w-full pb-10">
            {/* Header */}
            <ProposalOrderHeader
                proposalId={proposal?.id || activeProposalId}
                status={proposal?.status}
                onSave={handleInitiateConfirmOrder}
                isSaving={isSaving}
                canEdit={canEdit}
                isAlreadyOrdered={isAlreadyOrdered}
                isCancelled={isCancelled}
                isLoading={isLoading}
            />

            {/* Section 1: Order Overview (Client & Proposal Summary Card) */}
            <ProposalOrderClientCard
                proposal={proposal}
                isLoading={isLoading}
            />

            {/* Section 2: Proposal Order Wine List */}
            <ProposalOrderWineList
                wines={wines}
                financialSummary={proposal?.financial_summary}
                isLoading={isLoading}
            />

            {/* Section 3: Delivery & Order Details Form (Destinazione Merce) */}
            <ProposalOrderForm
                formData={formData}
                onFormChange={handleFormChange}
                provincesList={provincesList}
                addressMode={addressMode}
                onAddressModeChange={handleAddressModeChange}
                savedAddresses={savedAddresses}
                selectedAddressId={selectedAddressId}
                onSelectSavedAddress={handleSelectSavedAddress}
                errors={formErrors}
                validationTrigger={validationTrigger}
            />

            {/* Section 4: Payment Method (Condizione di Pagamento) */}
            <ProposalOrderPaymentMethod
                payment_conditions_code={formData.payment_conditions_code}
                onChange={handleFormChange}
                defaultPaymentCondition={defaultPaymentCondition}
                errors={formErrors}
                validationTrigger={validationTrigger}
            />

            {/* Section 5: Final Action - Order Confirmation (Luxury Card) */}
            {!isLoading && !isAlreadyOrdered && !isCancelled && (
                <div className="w-full bg-gradient-to-r from-white via-[#FCF9FA] to-[#FAF2F4] border-[1.5px] border-[#8B2433]/25 rounded-2xl p-5 sm:p-6 shadow-[0_6px_24px_rgba(139,36,51,0.06)] relative overflow-hidden mt-6">
                    {/* Top decorative gradient accent line */}
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#8B2433] via-[#D58C96] to-[#FAF3F4]" />

                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
                        {/* Left: Icon & Description */}
                        <div className="flex items-center gap-3.5 min-w-0">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FAF0F2] to-[#F5E6E8] border border-[#8B2433]/20 flex items-center justify-center shrink-0 text-[#8B2433] shadow-xs">
                                <RiShoppingBag3Line className="w-6 h-6" />
                            </div>
                            <div className="min-w-0">
                                <h4 className="text-base sm:text-lg font-bold poppins-semibold text-[#2E2E2E]">
                                    Conferma Finale Ordine
                                </h4>
                                <p className="text-xs sm:text-sm text-gray-500 poppins-regular mt-0.5 leading-relaxed">
                                    Verifica i dettagli dell&apos;ordine, la destinazione merce e le condizioni di pagamento prima di confermare.
                                </p>
                            </div>
                        </div>

                        {/* Right: Horizontally Aligned Luxury Action Buttons */}
                        <div className="flex items-center gap-3 w-full md:w-auto justify-end shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-[#8B2433]/10">
                            <button
                                type="button"
                                onClick={() => navigate("/admin/proposals")}
                                className="rounded-full py-2.5 px-5 sm:px-6 border-[1.5px] bg-white border-[#F2C3CA] text-[#8B2433] text-xs sm:text-sm font-semibold shadow-2xs poppins-medium whitespace-nowrap hover:bg-[#8B2433] hover:text-white hover:border-[#8B2433] transition-all cursor-pointer"
                            >
                                Torna indietro
                            </button>
                            {canEdit && (
                                <button
                                    type="button"
                                    onClick={handleInitiateConfirmOrder}
                                    disabled={isSaving}
                                    className="rounded-full py-2.5 px-6 sm:px-7 bg-gradient-to-r from-[#8B2433] to-[#A0283A] text-white text-xs sm:text-sm font-semibold shadow-[0_4px_16px_rgba(139,36,51,0.25)] hover:shadow-[0_6px_20px_rgba(139,36,51,0.35)] hover:brightness-105 active:scale-[0.98] transition-all cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                >
                                    <TiTick className="w-5 h-5 text-white shrink-0" />
                                    <span>Conferma Ordine</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            <ProposalOrderConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => !isSaving && setIsConfirmModalOpen(false)}
                onConfirm={handleExecuteConfirmOrder}
                isSaving={isSaving}
                companyName={modalCompanyName}
                addressTitle={modalAddress.title}
                fullAddress={modalAddress.full}
                addressEmail={modalAddress.email}
                addressPhone={modalAddress.phone}
                addressMobile={modalAddress.mobile}
                addressFax={modalAddress.fax}
                paymentMethodName={modalPaymentMethodName}
                totalBottles={modalTotalBottles}
                totalPrice={modalTotalPrice}
            />
        </div>
    );
};

OrdersProposal.propTypes = {
    hasPermission: PropTypes.func,
    proposalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default OrdersProposal;

